/**
 * Copies text to the clipboard and reports whether it actually worked.
 *
 * `navigator.clipboard` is undefined in non-secure contexts (plain HTTP) and
 * on older Safari, and even where it exists `writeText` can reject (denied
 * permission, no user gesture, etc). Callers that assume success from a
 * fulfilled promise — `Promise.resolve(navigator.clipboard?.writeText(url))`
 * fulfills with `undefined` when `clipboard` itself is missing — end up
 * showing "Link copied" when nothing was copied. This never throws and never
 * lies: it resolves `true` only when a copy method actually ran and
 * succeeded.
 *
 * Falls back to the legacy `execCommand("copy")` hidden-textarea trick when
 * the async Clipboard API isn't available, before giving up.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the legacy fallback below.
    }
  }

  const textarea = document.createElement("textarea");
  try {
    textarea.value = text;
    // Keep it off-screen and non-interactive but still selectable.
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);
    // Note: this clobbers any selection the user already had on the page,
    // even when the copy below fails. Accepted tradeoff — not worth the
    // complexity of saving/restoring the prior selection for this fallback.
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
};
